import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Student area: auth cookies are HttpOnly, we only need auto-refresh on 401.
export const studentApi = axios.create({
  baseURL: API,
  withCredentials: true,
});

let _refreshInFlight = null;
studentApi.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config || {};
    if (err.response?.status === 401 && !orig._retried && !orig.url?.includes("/auth/")) {
      orig._retried = true;
      try {
        _refreshInFlight = _refreshInFlight || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await _refreshInFlight;
        _refreshInFlight = null;
        return studentApi(orig);
      } catch (_) {
        _refreshInFlight = null;
      }
    }
    return Promise.reject(err);
  }
);

// Admin token management (localStorage: the token is a signed JWT with 7-day
// expiry, so the admin session survives page reloads and browser restarts).
export function getAdminToken() {
  return localStorage.getItem("mda_admin_token") || sessionStorage.getItem("mda_admin_token");
}

export function setAdminToken(token) {
  sessionStorage.removeItem("mda_admin_token");
  if (token) localStorage.setItem("mda_admin_token", token);
  else localStorage.removeItem("mda_admin_token");
}

export const adminApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

adminApi.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// If the token is expired/invalid, clear it and show the login screen
// instead of leaving tabs in a broken "empty data" state.
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/admin/login") && getAdminToken()) {
      setAdminToken(null);
      window.location.assign("/admin");
    }
    return Promise.reject(err);
  }
);

// ----- Checkout API -----
export async function createCheckoutSession({ productId, customerEmail, customerName, customerPhone }) {
  const res = await api.post("/checkout/session", {
    product_id: productId,
    origin_url: window.location.origin,
    customer_email: customerEmail || null,
    customer_name: customerName || null,
    customer_phone: customerPhone || null,
  });
  return res.data;
}

export async function createEmbeddedCheckoutSession({
  productId, customerEmail, customerName, customerPhone,
  billingAddress, billingCity, billingZip, billingProvince, billingCountry,
  wantsInvoice, codiceFiscale, ragioneSociale, partitaIva, codiceSdi, pecEmail,
  discountCode,
}) {
  const res = await api.post("/checkout/embedded-session", {
    product_id: productId,
    origin_url: window.location.origin,
    customer_email: customerEmail || null,
    customer_name: customerName || null,
    customer_phone: customerPhone || null,
    discount_code: discountCode || null,
    billing_address: billingAddress || null,
    billing_city: billingCity || null,
    billing_zip: billingZip || null,
    billing_province: billingProvince || null,
    billing_country: billingCountry || "IT",
    wants_invoice: !!wantsInvoice,
    codice_fiscale: codiceFiscale || null,
    ragione_sociale: ragioneSociale || null,
    partita_iva: partitaIva || null,
    codice_sdi: codiceSdi || null,
    pec_email: pecEmail || null,
  });
  return res.data; // { session_id, client_secret }
}

export async function getCheckoutStatus(sessionId) {
  const res = await api.get(`/checkout/status/${sessionId}`);
  return res.data;
}

// ----- Admin API -----
export async function adminLogin(password) {
  const res = await api.post("/admin/login", { password });
  setAdminToken(res.data.token);
  return res.data;
}

export async function adminLogout() {
  try {
    await adminApi.post("/admin/logout");
  } catch (e) {
    // Server-side logout is best-effort; local token cleanup still happens.
    console.warn("Admin logout request failed:", e?.response?.data || e?.message);
  }
  setAdminToken(null);
}

export async function adminVerify() {
  const res = await adminApi.get("/admin/verify");
  return res.data;
}

export async function adminStats() {
  const res = await adminApi.get("/admin/stats");
  return res.data;
}

export async function adminSalesDaily(days = 30) {
  const res = await adminApi.get("/admin/sales-daily", { params: { days } });
  return res.data;
}

export async function adminOrders() {
  const res = await adminApi.get("/admin/orders");
  return res.data;
}

export async function adminContacts() {
  const res = await adminApi.get("/admin/contacts");
  return res.data;
}

/** Download a CSV export from an admin endpoint (uses admin token via interceptor). */
export async function downloadAdminCsv(endpoint, filename) {
  const res = await adminApi.get(endpoint, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function adminRefundOrder(sessionId) {
  const res = await adminApi.post(`/admin/orders/${sessionId}/refund`);
  return res.data;
}

export async function adminBroadcast({ segment, subject, html_body, dry_run = false, scheduled_at = null }) {
  const res = await adminApi.post("/admin/broadcast", { segment, subject, html_body, dry_run, scheduled_at });
  return res.data;
}

export async function adminBroadcastPreview({ subject, html_body, sample_name }) {
  const res = await adminApi.post("/admin/broadcast/preview", { subject, html_body, sample_name });
  return res.data;
}

export async function adminBroadcastTest({ subject, html_body, to_email, sample_name }) {
  const res = await adminApi.post("/admin/broadcast/test", { subject, html_body, to_email, sample_name });
  return res.data;
}

export async function adminCreateContact(payload) {
  const res = await adminApi.post("/admin/contacts", payload);
  return res.data;
}

export async function adminUpdateContactStage(contactId, stage) {
  const res = await adminApi.patch(`/admin/contacts/${contactId}/stage`, { stage });
  return res.data;
}

export async function adminUpdateContact(contactId, payload) {
  const res = await adminApi.patch(`/admin/contacts/${contactId}`, payload);
  return res.data;
}

export async function adminGetActivities(contactId) {
  const res = await adminApi.get(`/admin/contacts/${contactId}/activities`);
  return res.data;
}

export async function adminAddActivity(contactId, payload) {
  const res = await adminApi.post(`/admin/contacts/${contactId}/activities`, payload);
  return res.data;
}

export async function adminUpdateActivity(activityId, payload) {
  const res = await adminApi.patch(`/admin/activities/${activityId}`, payload);
  return res.data;
}

export async function adminDeleteActivity(activityId) {
  const res = await adminApi.delete(`/admin/activities/${activityId}`);
  return res.data;
}

export async function adminPendingTasks() {
  const res = await adminApi.get("/admin/tasks/pending");
  return res.data;
}

export async function adminTransactions() {
  const res = await adminApi.get("/admin/transactions");
  return res.data;
}

export async function adminListMaterials() {
  const res = await adminApi.get("/admin/materials");
  return res.data;
}

export async function adminUploadMaterial({ product_id, title, file, onProgress }) {
  const form = new FormData();
  form.append("product_id", product_id);
  form.append("title", title);
  form.append("file", file);
  const res = await adminApi.post("/admin/materials/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress,
  });
  return res.data;
}

export async function adminUpdateMaterial(id, payload) {
  const res = await adminApi.patch(`/admin/materials/${id}`, payload);
  return res.data;
}

export async function adminDeleteMaterial(id) {
  const res = await adminApi.delete(`/admin/materials/${id}`);
  return res.data;
}

export async function adminContactSendEmail(contactId, { subject, html_body }) {
  const res = await adminApi.post(`/admin/contacts/${contactId}/send-email`, { subject, html_body });
  return res.data;
}

export async function adminContactLogWhatsApp(contactId, message) {
  const res = await adminApi.post(`/admin/contacts/${contactId}/log-whatsapp`, { message });
  return res.data;
}

export async function adminContactsBulkEmail({ contact_ids, subject, html_body }) {
  const res = await adminApi.post("/admin/contacts/bulk-email", { contact_ids, subject, html_body });
  return res.data;
}

export async function adminCampaigns() {
  const res = await adminApi.get("/admin/campaigns");
  return res.data;
}

export async function adminListStudents() {
  const res = await adminApi.get("/admin/students");
  return res.data;
}

export async function adminMarkOrderCompleted(sessionId) {
  const res = await adminApi.post(`/admin/orders/${sessionId}/complete`);
  return res.data;
}

export async function adminUnmarkOrderCompleted(sessionId) {
  const res = await adminApi.post(`/admin/orders/${sessionId}/uncomplete`);
  return res.data;
}

export async function adminOpenCertificatePreview(sessionId) {
  const res = await adminApi.get(`/admin/orders/${sessionId}/certificate/preview`, {
    responseType: "blob",
  });
  const blobUrl = URL.createObjectURL(res.data);
  window.open(blobUrl, "_blank");
  // Revoke after a delay so the tab has time to load
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

// -------- Codici sconto personalizzati (CRM) --------
export async function adminCreateDiscountCode({ email, pct, days_valid }) {
  const res = await adminApi.post("/admin/discount-codes", { email, pct, days_valid });
  return res.data;
}

export async function adminListDiscountCodes() {
  const res = await adminApi.get("/admin/discount-codes");
  return res.data;
}

export async function adminDeleteDiscountCode(code) {
  const res = await adminApi.delete(`/admin/discount-codes/${encodeURIComponent(code)}`);
  return res.data;
}

// -------- A/B test landing --------
export async function adminAbStats() {
  const res = await adminApi.get("/admin/ab-stats");
  return res.data;
}

// -------- Onboarding call bookings (Cal.com) --------
export async function adminOnboardingBookings() {
  const res = await adminApi.get("/admin/onboarding-bookings");
  return res.data;
}
